import React, { useEffect, useState } from 'react';
import { DollarSign, Users, TrendingUp, Package, ShoppingCart } from 'lucide-react';
import Loader from '../../components/common/Loader';
import { getProductDetail } from '../../services/store/productAxios';
import { formatCurrency, getCurrencySymbol } from '../../utils/formatters';
import { dictionary } from '../../utils/dictionary';
import { useAuth } from '../../context/AuthContext';

interface ProductStatsProps {
  type: string;
  revenue: number;
  visits: number;
  avgTime: string;
  conversion_rate: number;
  orders: number;
  product_id: string;
  visits_not_unique: number;
  units_sold: number;
}

const ProductStats: React.FC<ProductStatsProps> = ({
  type,
  revenue,
  visits,
  avgTime,
  orders,
  conversion_rate,
  product_id,
  units_sold
}) => {
  const [image, setImage] = useState<string>('');
  const [name, setName] = useState<string>('');
  const orderCount = Number.isFinite(Number(orders)) ? Number(orders) : 0;
  const uniqueVisitCount = Number.isFinite(Number(visits)) ? Number(visits) : 0;
  const unitCount = Number.isFinite(Number(units_sold)) ? Number(units_sold) : 0;
  const conversionWidth = Math.max(0, Math.min(Number(conversion_rate) || 0, 100));
  const conversionRaw = `${orderCount}/${uniqueVisitCount}`;
  const { language, country } = useAuth();
  const currencySymbol = getCurrencySymbol(country);

  useEffect(() => {
    getProductDetail(product_id)
      .then((product) => {
        setImage(product.images[0]);
        setName(product.name);
      });
  }, [product_id]);

  return (
    <div className="bg-white rounded-[20px] shadow-sm border border-gray-200 p-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-center">
        <div className="flex items-center gap-4 lg:col-span-4 xl:col-span-3">
          {image ? (
            <img
              src={image}
              alt={name}
              className="w-16 h-16 object-cover rounded-lg"
            />
          ) : (
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
              <Loader />
            </div>
          )}
          <div>
            <span className="inline-flex text-xs font-medium mb-1 px-2 py-0.5 rounded-full bg-blue-100 text-purple-700">
              {dictionary[type][language] || type}
            </span>
            {name ? (
              <h3 className="text-base font-semibold text-gray-900 line-clamp-2">{name}</h3>
            ) : (
              <Loader size={20} />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-8 xl:col-span-6 xl:grid-cols-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-full text-gray-500">
              <DollarSign size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{dictionary["revenue"][language]}</p>
              <p className="font-semibold">{formatCurrency(revenue, currencySymbol)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-full text-gray-500">
              <ShoppingCart size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{dictionary["orders-tab"]?.[language] || "Órdenes"}</p>
              <p className="font-semibold">{orderCount.toLocaleString()}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-full text-gray-500">
              <Package size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{dictionary["units-sold"]?.[language] || "Unidades vendidas"}</p>
              <p className="font-semibold">{unitCount.toLocaleString()}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-full text-gray-500">
              <Users size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{dictionary["visits-unique"][language]}</p>
              <p className="font-semibold">{uniqueVisitCount.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-12 xl:col-span-3">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gray-100 rounded-full text-gray-500">
              <TrendingUp size={16} />
            </div>
            <div>
              <p className="text-sm text-gray-500">{dictionary["conversion"][language]}</p>
              <div className="flex items-center gap-2">
                <p className="font-semibold">{conversion_rate}%</p>
                <span className="text-xs text-gray-400">({conversionRaw})</span>
              </div>
            </div>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ backgroundColor: '#6B48FF', width: `${conversionWidth}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductStats;
